import { useEffect, useRef, useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useDeadlineNotifications } from '@/hooks/useDeadlineNotifications';
import { DeadlineCalendarModal } from '@/components/deadlines/DeadlineCalendarModal';
import { ToastAction } from '@/components/ui/toast';
/**
 * Component that shows toast notifications for urgent deadlines
 * Should be mounted once in the app layout
 */
export function DeadlineToastNotifier() {
  const { toast } = useToast();
  const { notifications, isLoading } = useDeadlineNotifications();
  const hasShownInitial = useRef(false);
  const lastNotificationCount = useRef(0);
  const [calendarOpen, setCalendarOpen] = useState(false);

  useEffect(() => {
    if (isLoading || hasShownInitial.current) return;

    const urgentNotifications = notifications.filter(
      n => !n.read && (n.type === 'expired' || n.type === 'today' || n.type === 'urgent')
    );

    if (urgentNotifications.length > 0) {
      const expiredCount = urgentNotifications.filter(n => n.type === 'expired').length;
      const todayCount = urgentNotifications.filter(n => n.type === 'today').length;
      const urgentCount = urgentNotifications.filter(n => n.type === 'urgent').length;

      // Get the most urgent deadline name
      const mostUrgent = urgentNotifications[0];
      const deadlineName = mostUrgent?.deadline?.title || '';

      let title = '';
      let description = '';

      if (expiredCount > 0) {
        title = `⚠️ ${expiredCount} plazo${expiredCount > 1 ? 's' : ''} vencido${expiredCount > 1 ? 's' : ''}`;
        description = deadlineName
          ? `"${deadlineName}" requiere atención inmediata`
          : 'Tienes plazos que han expirado y requieren atención inmediata';
      } else if (todayCount > 0) {
        title = `🔴 ${todayCount} plazo${todayCount > 1 ? 's' : ''} vence${todayCount > 1 ? 'n' : ''} hoy`;
        description = deadlineName
          ? `"${deadlineName}" vence hoy`
          : 'Revisa tus plazos urgentes en el calendario';
      } else if (urgentCount > 0) {
        title = `🟠 ${urgentCount} plazo${urgentCount > 1 ? 's' : ''} urgente${urgentCount > 1 ? 's' : ''}`;
        description = deadlineName
          ? `"${deadlineName}" vence en los próximos 3 días`
          : 'Plazos que vencen en los próximos 3 días';
      }

      if (title) {
        toast({
          title,
          description,
          variant: expiredCount > 0 ? 'destructive' : 'default',
          action: (
            <ToastAction altText="Ver plazos" onClick={() => setCalendarOpen(true)}>
              Ver plazos
            </ToastAction>
          ),
        });
      }

      hasShownInitial.current = true;
      lastNotificationCount.current = urgentNotifications.length;
    } else {
      hasShownInitial.current = true;
    }
  }, [notifications, isLoading, toast]);

  // Also show toast when new urgent notifications appear
  useEffect(() => {
    if (isLoading || !hasShownInitial.current) return;

    const urgentNotifications = notifications.filter(
      n => !n.read && (n.type === 'expired' || n.type === 'today' || n.type === 'urgent')
    );

    const newCount = urgentNotifications.length;
    const prevCount = lastNotificationCount.current;

    if (newCount > prevCount && prevCount > 0) {
      const newest = urgentNotifications[0];
      toast({
        title: '📌 Nuevo plazo urgente',
        description: newest?.deadline?.title
          ? `"${newest.deadline.title}" - Tienes ${newCount} plazos que requieren atención`
          : `Tienes ${newCount} plazos que requieren atención`,
        action: (
          <ToastAction altText="Ver plazos" onClick={() => setCalendarOpen(true)}>
            Ver plazos
          </ToastAction>
        ),
      });
    }

    lastNotificationCount.current = newCount;
  }, [notifications, isLoading, toast]);

  return <DeadlineCalendarModal open={calendarOpen} onOpenChange={setCalendarOpen} />;
}
